/**
 * Optional dev fallbacks when linked-wallets API omits settlement metadata.
 * Production should use wallet.settlement from GET /wallet/v1/wallets/linked.
 */

const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;

const POLYGON_CHAIN_IDS = new Set([80002, 137]);
const ETH_CHAIN_IDS = new Set([1, 11155111]);
const BASE_CHAIN_IDS = new Set([8453, 84532]);

const DEFAULT_CHAIN_USDC = {
  80002: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582',
  84532: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',
};

const normalizeEvmAddress = (value) => {
  const trimmed = String(value ?? '').trim();
  return EVM_ADDRESS_PATTERN.test(trimmed) ? trimmed : null;
};

export const EVM_ENV_SPENDER_ADDRESS = normalizeEvmAddress(
  import.meta.env.VITE_APP_EVM_SPENDER_ADDRESS,
);

export const EVM_ENV_POLYGON_CONTRACT = normalizeEvmAddress(
  import.meta.env.VITE_APP_EVM_POLYGON_CONTRACT,
);

export const EVM_ENV_ETH_CONTRACT = normalizeEvmAddress(
  import.meta.env.VITE_APP_EVM_ETH_CONTRACT,
);

export const EVM_ENV_POLYGON_USDC = normalizeEvmAddress(
  import.meta.env.VITE_APP_EVM_POLYGON_USDC,
);

export const EVM_ENV_ETH_USDC = normalizeEvmAddress(
  import.meta.env.VITE_APP_EVM_ETH_USDC,
);

export const EVM_ENV_BASE_USDC = normalizeEvmAddress(
  import.meta.env.VITE_APP_EVM_BASE_USDC,
);

export const getEnvSettlementContract = (chainId) => {
  const id = Number(chainId);
  if (POLYGON_CHAIN_IDS.has(id)) return EVM_ENV_POLYGON_CONTRACT;
  if (ETH_CHAIN_IDS.has(id)) return EVM_ENV_ETH_CONTRACT;
  return null;
};

export const getEnvSpenderAddress = (chainId) =>
  EVM_ENV_SPENDER_ADDRESS ?? getEnvSettlementContract(chainId);

const isErc20Asset = (assetSymbol) => {
  const key = String(assetSymbol ?? '').trim().toLowerCase();
  if (!key) return false;
  if (key.includes(':')) return true;
  return key.includes('usdc') || key.includes('usdt');
};

export const getEnvTokenContract = (chainId, assetSymbol) => {
  if (!isErc20Asset(assetSymbol)) return null;

  const id = Number(chainId);
  if (POLYGON_CHAIN_IDS.has(id)) {
    return EVM_ENV_POLYGON_USDC ?? normalizeEvmAddress(DEFAULT_CHAIN_USDC[id]) ?? null;
  }
  if (ETH_CHAIN_IDS.has(id)) {
    return EVM_ENV_ETH_USDC ?? normalizeEvmAddress(DEFAULT_CHAIN_USDC[id]) ?? null;
  }
  if (BASE_CHAIN_IDS.has(id)) {
    return EVM_ENV_BASE_USDC ?? normalizeEvmAddress(DEFAULT_CHAIN_USDC[id]) ?? null;
  }
  return null;
};

export const getEnvTokenDecimals = () => null;

export const isEnvSettlementAddress = (address, chainId) => {
  const candidate = normalizeEvmAddress(address);
  if (!candidate) return false;

  const settlementKeys = new Set(
    [EVM_ENV_SPENDER_ADDRESS, getEnvSettlementContract(chainId)]
      .filter(Boolean)
      .map((value) => value.toLowerCase()),
  );

  return settlementKeys.has(candidate.toLowerCase());
};
